// src/pages/subscription/SubscriptionAuditLogPage.tsx
// Subscription audit history, served by the audittrail service:
// per-subscription via ListAuditRecordsByResource (resource URN built from the
// subscription's entity), or a platform-wide subscription search via
// SearchAuditRecords when no subscription is selected.

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useSubscription, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { ListAuditRecordsByResource } from '@core/api/endpoints/audittrail/list_audit_records_by_resource';
import { SearchAuditRecords } from '@core/api/endpoints/audittrail/search_audit_records';
import type { AuditRecord } from '@core/types/audittrail';
import { entityToUrn } from '@/features/resources/logic';
import { AUDIT_ACTION_VALUES, paginationParams } from '@/features/audittrail/logic';

function formatLabel(value: string, prefix: string): string {
  return value.replace(prefix, '').split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(dateStr); }
}

function getActionBadgeVariant(action: string): string {
  switch (action) {
    case 'AUDIT_ACTION_CREATE':
    case 'AUDIT_ACTION_APPROVE':
      return 'success';
    case 'AUDIT_ACTION_UPDATE':
    case 'AUDIT_ACTION_EXPORT':
    case 'AUDIT_ACTION_SHARE':
      return 'info';
    case 'AUDIT_ACTION_DELETE':
    case 'AUDIT_ACTION_REJECT':
      return 'error';
    case 'AUDIT_ACTION_ESCALATE':
      return 'warning';
    default:
      return 'secondary';
  }
}

function truncateValue(value: string | null | undefined, maxLen: number = 60): string {
  if (!value) return '--';
  const str = String(value);
  return str.length > maxLen ? str.substring(0, maxLen) + '...' : str;
}

export const SubscriptionAuditLogPage = observer(() => {
  const { id: routeSubscriptionId } = useParams<{ id?: string }>();
  const store = useSubscription();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  // The subscription whose audit trail is shown: route param when the page is
  // opened per-subscription, ?subscription= query param otherwise.
  const subscriptionId =
    routeSubscriptionId || searchParams.get('subscription') || '';

  const [logs, setLogs] = useState<AuditRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [filterAction, setFilterAction] = useState(searchParams.get('action') || '');
  const [filterDateFrom, setFilterDateFrom] = useState(searchParams.get('from') || '');
  const [filterDateTo, setFilterDateTo] = useState(searchParams.get('to') || '');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const appliedAction = searchParams.get('action') || '';
  const appliedFrom = searchParams.get('from') || '';
  const appliedTo = searchParams.get('to') || '';

  useEffect(() => {
    let cancelled = false;
    const loadLogs = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const pagination = paginationParams(50);
        let response;
        if (subscriptionId) {
          // Resolve the subscription so its Entity can be turned into a URN.
          const subscription = await store.GetSubscription(subscriptionId);
          const urn = entityToUrn(subscription?.entity);
          if (!urn) throw new Error(t('subscription.auditLog'));
          response = await ListAuditRecordsByResource(urn, pagination);
        } else {
          response = await SearchAuditRecords({
            query: 'subscription',
            pagination,
            from: appliedFrom ? new Date(appliedFrom).toISOString() : undefined,
            to: appliedTo ? new Date(appliedTo).toISOString() : undefined,
            action: (appliedAction || undefined) as AuditRecord['action'],
          });
        }
        let rows = Array.isArray(response.items?.items) ? response.items.items : [];
        // ListAuditRecordsByResource accepts only pagination, so action and
        // date filters are applied client-side for consistent behaviour on
        // both query paths.
        if (appliedAction) rows = rows.filter((r) => r.action === appliedAction);
        if (appliedFrom) rows = rows.filter((r) => !r.occurred_at || new Date(r.occurred_at) >= new Date(appliedFrom));
        if (appliedTo) rows = rows.filter((r) => !r.occurred_at || new Date(r.occurred_at) <= new Date(appliedTo));
        if (!subscriptionId) rows = rows.filter((r) => (r.resource_urn || '').includes('subscription'));
        if (!cancelled) setLogs(rows);
      } catch (e) {
        if (!cancelled) {
          setLogs([]);
          setLoadError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadLogs();
    return () => { cancelled = true; };
  }, [subscriptionId, appliedAction, appliedFrom, appliedTo]);

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    const params: Record<string, string> = {};
    if (!routeSubscriptionId && subscriptionId) params.subscription = subscriptionId;
    if (filterAction) params.action = filterAction;
    if (filterDateFrom) params.from = filterDateFrom;
    if (filterDateTo) params.to = filterDateTo;
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    setFilterAction('');
    setFilterDateFrom('');
    setFilterDateTo('');
    const params: Record<string, string> = {};
    if (!routeSubscriptionId && subscriptionId) params.subscription = subscriptionId;
    setSearchParams(params);
  };

  const toggleExpanded = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* The tab already names this page; the heading stays for screen readers,
          and what it used to explain now sits in the empty state. */}
      <div>
        <h1 className="sr-only">{t('subscription.auditLog')}</h1>
        <nav className="text-sm">
          <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground">
            {t('subscription.list')}
          </Link>
          {subscriptionId && (
            <>
              <span className="mx-2 text-muted-foreground">/</span>
              <Link to={`/subscriptions/${subscriptionId}`} className="text-muted-foreground hover:text-foreground">
                {t('subscription.details')}
              </Link>
            </>
          )}
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('subscription.auditLog')}</span>
        </nav>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}
      {loadError ? <ErrorAlert error={loadError} dismissible onDismiss={() => setLoadError(null)} /> : null}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleFilter} className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-foreground mb-1">
                {t('subscription.actionType')}
              </label>
              <Select value={filterAction} onChange={(e) => setFilterAction(e.target.value)}>
                <option value="">{t('common.all')}</option>
                {AUDIT_ACTION_VALUES.map((action) => (
                  <option key={action} value={action}>
                    {formatLabel(action, 'AUDIT_ACTION_')}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Input
                label={t('subscription.dateFrom')}
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
              />
            </div>
            <div>
              <Input
                label={t('subscription.dateTo')}
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" variant="secondary">
                {t('common.filter')}
              </Button>
              <Button type="button" variant="ghost" onClick={handleResetFilters}>
                {t('common.reset')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Results count */}
      {!isLoading && logs.length > 0 && (
        <p className="text-sm text-muted-foreground">
          {logs.length} {t('subscription.auditEntries')}
        </p>
      )}

      {/* Table */}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('subscription.date')}</TableHead>
              <TableHead>{t('subscription.action')}</TableHead>
              <TableHead>{t('subscription.actor')}</TableHead>
              <TableHead>{t('subscription.targetEntity')}</TableHead>
              <TableHead>{t('subscription.oldValue')}</TableHead>
              <TableHead>{t('subscription.newValue')}</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableEmpty colSpan={7}>
                <span className="table-empty-message">{t('subscription.noAuditEntries')}</span>
                <p className="mt-1 text-sm text-muted-foreground">{t('subscription.auditLogDescription')}</p>
              </TableEmpty>
            ) : (
              logs.map((log) => {
                const logId = log.entity?.id || `${log.sequence_number}-${log.record_hash}`;
                const isExpanded = expandedRows.has(logId);
                const action = log.action ?? '';
                const variant = getActionBadgeVariant(action);

                return (
                  <TableRow key={logId}>
                    <TableCell>
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(log.occurred_at)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div>
                        <Badge variant={variant as any} size="sm">
                          {formatLabel(action, 'AUDIT_ACTION_')}
                        </Badge>
                        {log.event_type && (
                          <span className="text-xs text-muted-foreground block mt-0.5">
                            {String(log.event_type)}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {log.actor?.display_name || log.actor?.entity?.id || '--'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-mono text-muted-foreground break-all">
                        {log.resource_urn || '--'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {isExpanded ? (
                        <pre className="text-xs text-muted-foreground whitespace-pre-wrap break-all max-w-xs bg-muted p-2 rounded">
                          {log.before_state || '--'}
                        </pre>
                      ) : (
                        <span className="text-xs text-muted-foreground truncate max-w-[150px] block">
                          {truncateValue(log.before_state)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isExpanded ? (
                        <pre className="text-xs text-foreground whitespace-pre-wrap break-all max-w-xs bg-muted p-2 rounded">
                          {log.after_state || '--'}
                        </pre>
                      ) : (
                        <span className="text-xs text-foreground truncate max-w-[150px] block">
                          {truncateValue(log.after_state)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {(log.before_state || log.after_state) && (
                        <button
                          onClick={() => toggleExpanded(logId)}
                          className="text-xs text-primary hover:text-primary/80 transition-colors whitespace-nowrap"
                        >
                          {isExpanded ? t('common.hide') : t('common.show')}
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default SubscriptionAuditLogPage;
